import React, { useState, useEffect, useCallback } from 'react';
import { axios } from '../../context/AuthContext';
import { useAuth } from '../../context/AuthContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import toast from 'react-hot-toast';

const TUM_GUNLER = ['Pazartesi', 'Sali', 'Carsamba', 'Persembe', 'Cuma', 'Cumartesi', 'Pazar'];
const GUN_ETIKETLERI = {
  Pazartesi: 'Pazartesi',
  Sali: 'Salı',
  Carsamba: 'Çarşamba',
  Persembe: 'Perşembe',
  Cuma: 'Cuma',
  Cumartesi: 'Cumartesi',
  Pazar: 'Pazar',
};
const GUN_KISA = {
  Pazartesi: 'Pzt',
  Sali: 'Sal',
  Carsamba: 'Çar',
  Persembe: 'Per',
  Cuma: 'Cum',
  Cumartesi: 'Cmt',
  Pazar: 'Paz',
};
const VARSAYILAN_TENEFUSLER = [
  { tip: 'tenefus', bas: '09:40', bit: '09:50' },
  { tip: 'tenefus', bas: '10:30', bit: '10:40' },
  { tip: 'tenefus', bas: '11:20', bit: '11:30' },
  { tip: 'ogle', bas: '12:10', bit: '13:00' },
  { tip: 'tenefus', bas: '13:40', bit: '13:50' },
  { tip: 'tenefus', bas: '14:30', bit: '14:40' },
  { tip: 'tenefus', bas: '15:20', bit: '15:30' },
];
const VARSAYILAN_GUN_SAATLERI = {
  Pazartesi: { bas: '09:00', bit: '17:00', aktif: true },
  Sali: { bas: '09:00', bit: '17:00', aktif: true },
  Carsamba: { bas: '09:00', bit: '17:00', aktif: true },
  Persembe: { bas: '09:00', bit: '17:00', aktif: true },
  Cuma: { bas: '09:00', bit: '17:00', aktif: true },
  Cumartesi: { bas: '09:00', bit: '17:00', aktif: true },
  Pazar: { bas: '09:00', bit: '17:00', aktif: true },
};

function dakikaYap(saat = '00:00') {
  const [h, m] = saat.split(':').map(Number);
  return h * 60 + m;
}

function saatStr(h, m) {
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function gunIcinSaatlerUret(basDk, bitDk, sureDk, tenefusler) {
  const saatler = [];
  let current = basDk;

  while (current + sureDk <= bitDk) {
    const slotBit = current + sureDk;
    const cakisan = tenefusler.find((t) => {
      const tBas = dakikaYap(t.bas);
      const tBit = dakikaYap(t.bit);
      return current < tBit && slotBit > tBas;
    });

    if (!cakisan) {
      saatler.push({
        bas: saatStr(Math.floor(current / 60), current % 60),
        bit: saatStr(Math.floor(slotBit / 60), slotBit % 60),
      });
      current = slotBit;
    } else {
      current = Math.max(current + 1, dakikaYap(cakisan.bit));
    }
  }

  return saatler;
}

function tumGunlerIcinSaatler(gunSaatleri, sureDk, tenefusler) {
  const result = {};
  TUM_GUNLER.forEach((gun) => {
    const ayar = gunSaatleri?.[gun];
    if (!ayar?.aktif) return;
    result[gun] = gunIcinSaatlerUret(
      dakikaYap(ayar.bas),
      dakikaYap(ayar.bit),
      sureDk,
      tenefusler
    );
  });
  return result;
}

function normalizeGunSlotlari(raw = {}) {
  const result = {};
  TUM_GUNLER.forEach((gun) => {
    if (!Array.isArray(raw?.[gun])) return;
    result[gun] = raw[gun]
      .filter((slot) => slot?.bas && slot?.bit)
      .map((slot) => ({ bas: slot.bas.slice(0, 5), bit: slot.bit.slice(0, 5) }));
  });
  return result;
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
}

// ── Yoklama Sayfası ───────────────────────────────────────────
export function YoklamaPage() {
  const [siniflar, setSiniflar] = useState([]);
  const [dersler, setDersler] = useState([]);
  const [gecmis, setGecmis] = useState([]);
  const [aktifYoklama, setAktifYoklama] = useState(null);
  const [aktifKayitlar, setAktifKayitlar] = useState([]);
  const [form, setForm] = useState({ sinif_id:'', ders_id:'', sure:30 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get('/siniflar'),
      axios.get('/dersler'),
      axios.get('/yoklama/ogretmen/gecmis'),
    ]).then(([sRes, dRes, gRes]) => {
      if (sRes.data.success) setSiniflar(sRes.data.data);
      if (dRes.data.success) setDersler(dRes.data.data);
      if (gRes.data.success) setGecmis(gRes.data.data);
    }).finally(() => setLoading(false));
  }, []);

  const loadKayitlar = async (yoklamaId) => {
    const { data } = await axios.get(`/yoklama/${yoklamaId}`);
    if (data.success) setAktifKayitlar(data.data.kayitlar || []);
  };

  const handleAc = async () => {
    if (!form.sinif_id || !form.ders_id) { toast.error('Sınıf ve ders seçin'); return; }
    try {
      const { data } = await axios.post('/yoklama/ac', { ...form, sure_dakika: form.sure });
      if (data.success) {
        setAktifYoklama(data.data);
        toast.success(`Yoklama açıldı! Kod: ${data.data.kod}`);
        await loadKayitlar(data.data.id);
      }
    } catch { toast.error('Yoklama açılamadı'); }
  };

  const handleDurumGuncelle = async (yoklamaId, ogrenciId, durum) => {
    await axios.put(`/yoklama/${yoklamaId}/kayit/${ogrenciId}`, { durum });
    setAktifKayitlar(prev => prev.map(k => k.ogrenci_id === ogrenciId ? { ...k, durum } : k));
    if (durum === 'devamsiz') toast.success('Veliye bildirim gönderildi');
  };

  const handleKapat = async (yoklamaId) => {
    await axios.put(`/yoklama/${yoklamaId}/kapat`);
    toast.success('Yoklama kapatıldı, velilere bildirim gönderildi');
    setAktifYoklama(null);
    setAktifKayitlar([]);
    const { data } = await axios.get('/yoklama/ogretmen/gecmis');
    if (data.success) setGecmis(data.data);
  };

  const DURUM_COLORS = { mevcut:'#10B981', devamsiz:'#EF4444', gec:'#F97316', izinli:'#3B82F6' };
  const DURUM_LABELS = { mevcut:'✅ Mevcut', devamsiz:'❌ Devamsız', gec:'⏰ Geç', izinli:'📋 İzinli' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div className="animate-fadeInUp">
        <h2 style={{ fontFamily: 'var(--font-ui)', fontSize: '1.3rem' }}>✅ Yoklama Sistemi</h2>
      </div>

      {aktifYoklama ? (
        /* Aktif Yoklama */
        <div className="animate-fadeIn">
          <div style={{
            background: 'rgba(16,185,129,0.06)', border: '2px solid rgba(16,185,129,0.3)',
            borderRadius: 20, padding: '24px', marginBottom: 20,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16,
          }}>
            <div>
              <div style={{ fontFamily: 'var(--font-ui)', fontWeight: 700, color: '#10B981', marginBottom: 4 }}>
                ✅ Yoklama Aktif
              </div>
              <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                Geçerlilik: {new Date(aktifYoklama.gecerlilik).toLocaleTimeString('tr-TR')}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: 'var(--font-ui)', fontWeight: 800, fontSize: '3rem', color: '#10B981', letterSpacing: '0.25em', lineHeight: 1 }}>
                {aktifYoklama.kod}
              </div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>Öğrencilere gösterin</div>
            </div>
            <button className="btn btn-danger" onClick={() => handleKapat(aktifYoklama.id)}>
              Yoklamayı Kapat
            </button>
          </div>

          <div className="card">
            <div className="section-header" style={{ marginBottom: 14 }}>
              <div className="section-title">Katılım Listesi</div>
              <div style={{ display: 'flex', gap: 12, fontSize: '0.82rem' }}>
                <span style={{ color: '#10B981' }}>✅ {aktifKayitlar.filter(k => k.durum === 'mevcut').length} Mevcut</span>
                <span style={{ color: '#EF4444' }}>❌ {aktifKayitlar.filter(k => k.durum === 'devamsiz').length} Devamsız</span>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {aktifKayitlar.map((k, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                  background: 'var(--bg-elevated)', borderRadius: 10,
                  borderLeft: `3px solid ${DURUM_COLORS[k.durum] || '#94A3B8'}`,
                }}>
                  <div className="avatar avatar-sm" style={{ background: (DURUM_COLORS[k.durum] || '#94A3B8') + '20', color: DURUM_COLORS[k.durum] || '#94A3B8' }}>
                    {k.ad?.[0]}{k.soyad?.[0]}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{k.ad} {k.soyad}</div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{k.ogrenci_no}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {Object.entries(DURUM_LABELS).map(([d, l]) => (
                      <button key={d}
                        style={{
                          padding: '4px 10px', borderRadius: 8, fontSize: '0.75rem', fontWeight: 600,
                          cursor: 'pointer', fontFamily: 'var(--font-ui)',
                          background: k.durum === d ? DURUM_COLORS[d] + '25' : 'transparent',
                          border: `1px solid ${k.durum === d ? DURUM_COLORS[d] + '50' : 'var(--border)'}`,
                          color: k.durum === d ? DURUM_COLORS[d] : 'var(--text-muted)',
                        }}
                        onClick={() => handleDurumGuncelle(aktifYoklama.id, k.ogrenci_id, d)}
                      >{l}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        /* Yoklama Aç Formu */
        <div className="grid-2 animate-fadeInUp stagger-1">
          <div className="card">
            <div className="section-title" style={{ marginBottom: 16 }}>+ Yeni Yoklama Aç</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="form-group">
                <label className="form-label">Sınıf *</label>
                <select className="form-select" value={form.sinif_id} onChange={e => setForm({...form, sinif_id: e.target.value})}>
                  <option value="">Sınıf seçin</option>
                  {siniflar.map(s => <option key={s.id} value={s.id}>{s.ad}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Ders *</label>
                <select className="form-select" value={form.ders_id} onChange={e => setForm({...form, ders_id: e.target.value})}>
                  <option value="">Ders seçin</option>
                  {dersler.map(d => <option key={d.id} value={d.id}>{d.ad}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Süre (dk)</label>
                <input className="form-input" type="number" value={form.sure} min={5} max={120}
                  onChange={e => setForm({...form, sure: e.target.value})} />
              </div>
              <button className="btn btn-primary" style={{ justifyContent: 'center' }} onClick={handleAc}>
                ✅ Yoklamayı Başlat
              </button>
            </div>
          </div>

          <div className="card">
            <div className="section-title" style={{ marginBottom: 16 }}>📋 Geçmiş Yoklamalar</div>
            {loading ? <div className="page-loader"><div className="spinner" /></div> :
              gecmis.length === 0 ? <div className="empty-state"><p>Henüz yoklama açılmadı</p></div> :
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 400, overflowY: 'auto' }}>
                {gecmis.map((y, i) => (
                  <div key={i} style={{ padding: '10px 14px', background: 'var(--bg-elevated)', borderRadius: 10, border: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{y.ders_adi}</span>
                      <span className={`badge ${y.durum === 'kapali' ? 'badge-muted' : 'badge-success'}`}>
                        {y.durum === 'kapali' ? 'Kapalı' : 'Açık'}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                      {y.sinif_adi} · {new Date(y.tarih).toLocaleDateString('tr-TR')} · 
                      {y.mevcut_sayisi || 0}/{y.toplam_ogrenci || 0} katıldı
                    </div>
                  </div>
                ))}
              </div>
            }
          </div>
        </div>
      )}
    </div>
  );
}

// ── Ödev Yönetimi ─────────────────────────────────────────────
export function OdevlerPage() {
  const [odevler, setOdevler] = useState([]);
  const [siniflar, setSiniflar] = useState([]);
  const [dersler, setDersler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ sinif_id:'', ders_id:'', baslik:'', aciklama:'', verilis_tarihi: new Date().toISOString().slice(0,10), teslim_tarihi:'' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [oRes, sRes, dRes] = await Promise.all([
        axios.get('/odevler'),
        axios.get('/siniflar'),
        axios.get('/dersler'),
      ]);
      if (oRes.data.success) setOdevler(oRes.data.data);
      if (sRes.data.success) setSiniflar(sRes.data.data);
      if (dRes.data.success) setDersler(dRes.data.data);
    } catch { toast.error('Yükleme hatası'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.ders_id || !form.baslik || !form.teslim_tarihi) {
      toast.error('Ders, başlık ve teslim tarihi zorunlu'); return;
    }
    try {
      await axios.post('/odevler', form);
      toast.success('Ödev oluşturuldu!');
      setShowModal(false);
      setForm({ sinif_id:'', ders_id:'', baslik:'', aciklama:'', verilis_tarihi: new Date().toISOString().slice(0,10), teslim_tarihi:'' });
      load();
    } catch { toast.error('Hata'); }
  };

  const handleTeslim = async (odevId, ogrenciId, durum) => {
    await axios.put(`/odevler/${odevId}/teslim`, { ogrenci_id: ogrenciId, durum });
    if (durum === 'teslim_edilmedi') toast.success('Veliye bildirim gönderildi');
    load();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div className="section-header animate-fadeInUp">
        <div>
          <h2 style={{ fontFamily: 'var(--font-ui)', fontSize: '1.3rem' }}>📝 Ödev Yönetimi</h2>
          <div className="section-subtitle">{odevler.length} ödev</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ Yeni Ödev</button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }} className="animate-fadeInUp stagger-1">
        {loading ? <div className="page-loader"><div className="spinner" /></div> :
          odevler.length === 0 ? (
            <div className="card"><div className="empty-state"><div style={{ fontSize: '2.5rem' }}>📝</div><h3>Ödev yok</h3><p>Henüz ödev oluşturulmadı.</p></div></div>
          ) : odevler.map((o, i) => {
            const teslimOrani = o.toplam_ogrenci > 0 ? (o.teslim_eden / o.toplam_ogrenci) * 100 : 0;
            const gecti = new Date(o.teslim_tarihi) < new Date();
            return (
              <div key={i} className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <h3 style={{ fontFamily: 'var(--font-ui)', fontSize: '1rem', marginBottom: 4 }}>{o.baslik}</h3>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <span className="badge badge-info">{o.ders_adi}</span>
                      {o.sinif_adi && <span className="badge badge-muted">{o.sinif_adi}</span>}
                      <span className={`badge ${gecti ? 'badge-danger' : 'badge-success'}`}>
                        {gecti ? '⏰ Süre Doldu' : `Teslim: ${new Date(o.teslim_tarihi).toLocaleDateString('tr-TR')}`}
                      </span>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: 'var(--font-ui)', fontWeight: 800, fontSize: '1.2rem', color: 'var(--accent)' }}>
                      {o.teslim_eden || 0}/{o.toplam_ogrenci || 0}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>teslim</div>
                  </div>
                </div>
                {o.aciklama && <p style={{ fontSize: '0.82rem', marginBottom: 12 }}>{o.aciklama}</p>}
                <div className="progress-bar" style={{ marginBottom: 8 }}>
                  <div className="progress-fill" style={{ width: `${teslimOrani}%` }} />
                </div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: 4 }}>
                  Öğretmen: {o.ogretmen_adi} · Verildi: {new Date(o.verilis_tarihi).toLocaleDateString('tr-TR')}
                </div>
              </div>
            );
          })
        }
      </div>

      {showModal && (
        <div className="modal-backdrop" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">📝 Yeni Ödev</div>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="form-group">
                <label className="form-label">Başlık *</label>
                <input className="form-input" value={form.baslik} placeholder="Ödev başlığı"
                  onChange={e => setForm({...form, baslik: e.target.value})} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Ders *</label>
                  <select className="form-select" value={form.ders_id} onChange={e => setForm({...form, ders_id: e.target.value})}>
                    <option value="">Ders seçin</option>
                    {dersler.map(d => <option key={d.id} value={d.id}>{d.ad}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Sınıf</label>
                  <select className="form-select" value={form.sinif_id} onChange={e => setForm({...form, sinif_id: e.target.value})}>
                    <option value="">Sınıf seçin</option>
                    {siniflar.map(s => <option key={s.id} value={s.id}>{s.ad}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Veriliş Tarihi</label>
                  <input className="form-input" type="date" value={form.verilis_tarihi}
                    onChange={e => setForm({...form, verilis_tarihi: e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Teslim Tarihi *</label>
                  <input className="form-input" type="date" value={form.teslim_tarihi}
                    onChange={e => setForm({...form, teslim_tarihi: e.target.value})} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Açıklama</label>
                <textarea className="form-textarea" value={form.aciklama} placeholder="Ödev detayları..."
                  onChange={e => setForm({...form, aciklama: e.target.value})} />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={handleCreate}>
                  ✓ Ödev Oluştur
                </button>
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>İptal</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function OgretmenProgramPage() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(true);
  const [program, setProgram] = useState([]);
  const [atananDersler, setAtananDersler] = useState([]);
  const [gunSaatleri, setGunSaatleri] = useState(VARSAYILAN_GUN_SAATLERI);
  const [gunSlotlari, setGunSlotlari] = useState({});
  const [dersSuresiDk, setDersSuresiDk] = useState(40);
  const [mobilGun, setMobilGun] = useState('Pazartesi');

  useEffect(() => {
    if (!user?.id) return;

    const load = async () => {
      setLoading(true);
      try {
        const [programRes, ayarRes, atananRes] = await Promise.all([
          axios.get('/ders-programi', { params: { ogretmen_id: user.id } }),
          axios.get('/program-ayarlari'),
          axios.get('/ogretmen/atanan-dersler'),
        ]);

        const programData = programRes.data?.success ? (programRes.data.data || []) : [];
        const ayarData = ayarRes.data?.success ? (ayarRes.data.data || {}) : {};
        const dersSuresi = Number(ayarData.ders_suresi_dk) || 40;
        const tenefusler = Array.isArray(ayarData.tenefusler) && ayarData.tenefusler.length > 0
          ? ayarData.tenefusler
          : VARSAYILAN_TENEFUSLER;
        const saatler = ayarData.gun_saatleri && Object.keys(ayarData.gun_saatleri).length > 0
          ? ayarData.gun_saatleri
          : VARSAYILAN_GUN_SAATLERI;
        const slotlar = normalizeGunSlotlari(ayarData.gun_slotlari);

        setProgram(programData);
        setAtananDersler(atananRes.data?.success ? (atananRes.data.data || []) : []);
        setDersSuresiDk(dersSuresi);
        setGunSaatleri(saatler);
        setGunSlotlari(
          Object.keys(slotlar).length > 0 ? slotlar : tumGunlerIcinSaatler(saatler, dersSuresi, tenefusler)
        );
      } catch (err) {
        toast.error(err.response?.data?.message || 'Ders programı yüklenemedi');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [user]);

  const aktifGunler = TUM_GUNLER.filter((gun) => gunSaatleri?.[gun]?.aktif);
  const tumSlotlar = [];
  aktifGunler.forEach((gun) => {
    (gunSlotlari[gun] || []).forEach((slot) => {
      if (!tumSlotlar.some((item) => item.bas === slot.bas && item.bit === slot.bit)) {
        tumSlotlar.push(slot);
      }
    });
  });
  tumSlotlar.sort((a, b) => dakikaYap(a.bas) - dakikaYap(b.bas));

  const programMap = {};
  program.forEach((item) => {
    programMap[`${item.gun}_${item.baslangic_saati}`] = item;
  });

  const bugunIndex = new Date().getDay();
  const jsGunMap = ['Pazar', 'Pazartesi', 'Sali', 'Carsamba', 'Persembe', 'Cuma', 'Cumartesi'];
  const bugun = jsGunMap[bugunIndex];
  const bugunDersleri = program
    .filter((ders) => ders.gun === bugun)
    .sort((a, b) => dakikaYap(a.baslangic_saati) - dakikaYap(b.baslangic_saati));
  const sinifSayisi = new Set(program.map((item) => item.sinif_id)).size;
  const haftalikDakika = program.length * dersSuresiDk;
  const haftalikSaatMetni = `${Math.floor(haftalikDakika / 60)}s ${haftalikDakika % 60}dk`;
  const gunlukOzet = aktifGunler.map((gun) => ({
    gun,
    adet: program.filter((ders) => ders.gun === gun).length,
  }));
  const enYogunGun = [...gunlukOzet].sort((a, b) => b.adet - a.adet)[0];
  const aktifMobilGun = aktifGunler.includes(mobilGun) ? mobilGun : (aktifGunler[0] || 'Pazartesi');
  const gorunenGunler = isMobile ? [aktifMobilGun] : aktifGunler;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        className="animate-fadeInUp"
        style={{
          background: 'linear-gradient(135deg, #17315C 0%, #244A8F 52%, #0EA5A4 100%)',
          borderRadius: 24,
          padding: isMobile ? '16px 14px' : '22px 24px',
          color: '#fff',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 14px 32px rgba(26, 54, 93, 0.18)',
        }}
      >
        <div
          style={{
            position: 'absolute',
            right: -40,
            top: -55,
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.08)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            right: 74,
            bottom: -76,
            width: 180,
            height: 180,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.06)',
          }}
        />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ fontSize: '0.7rem', letterSpacing: '0.14em', textTransform: 'uppercase', opacity: 0.72, fontWeight: 800 }}>
            Ogretmen Programi
          </div>
          <div
            style={{
              marginTop: 8,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              gap: 14,
              flexWrap: 'wrap',
            }}
          >
            <div>
              <h2 style={{ margin: 0, fontFamily: 'var(--font-ui)', fontSize: isMobile ? '1.3rem' : '1.72rem', lineHeight: 1.05, fontWeight: 800 }}>
                {user?.ad} {user?.soyad}
              </h2>
              <div style={{ marginTop: 6, fontSize: isMobile ? '0.8rem' : '0.88rem', color: 'rgba(255,255,255,0.84)', maxWidth: 700 }}>
                Sadece size atanmış dersler gösterilir. Haftalık yükünüzü, bugünkü akışınızı ve tüm çizelgeyi tek ekranda izleyebilirsiniz.
              </div>
            </div>
            <div
              style={{
                padding: '10px 14px',
                borderRadius: 16,
                background: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.16)',
                minWidth: isMobile ? '100%' : 190,
              }}
            >
              <div style={{ fontSize: '0.68rem', opacity: 0.72, marginBottom: 4 }}>En Yogun Gun</div>
              <div style={{ fontFamily: 'var(--font-ui)', fontSize: '1rem', fontWeight: 800 }}>
                {enYogunGun?.adet ? GUN_ETIKETLERI[enYogunGun.gun] : 'Program yok'}
              </div>
              <div style={{ fontSize: '0.74rem', opacity: 0.84, marginTop: 3 }}>
                {enYogunGun?.adet ? `${enYogunGun.adet} ders saati` : 'Ders tanimi bulunmuyor'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="page-loader"><div className="spinner" /></div>
      ) : (
        <>
          <div
            className="animate-fadeInUp stagger-1"
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}
          >
            <div className="stat-card" style={{ '--stat-color': '#0EA5A4', padding: 16 }}>
              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Aktif Gün</div>
              <div style={{ fontFamily: 'var(--font-ui)', fontSize: '1.7rem', fontWeight: 900 }}>{aktifGunler.length}</div>
              <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginTop: 4 }}>Programin acik oldugu gun sayisi</div>
            </div>
            <div className="stat-card" style={{ '--stat-color': '#3B82F6', padding: 16 }}>
              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Bugunku Ders</div>
              <div style={{ fontFamily: 'var(--font-ui)', fontSize: '1.7rem', fontWeight: 900 }}>{bugunDersleri.length}</div>
              <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginTop: 4 }}>{GUN_ETIKETLERI[bugun] || 'Bugun'} gunu icin planlanan dersler</div>
            </div>
            <div className="stat-card" style={{ '--stat-color': '#F97316', padding: 16 }}>
              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Atanmis Sinif</div>
              <div style={{ fontFamily: 'var(--font-ui)', fontSize: '1.7rem', fontWeight: 900 }}>{sinifSayisi}</div>
              <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginTop: 4 }}>Bu hafta ders verdiginiz sinif sayisi</div>
            </div>
            <div className="stat-card" style={{ '--stat-color': '#7C3AED', padding: 16 }}>
              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Haftalik Yuk</div>
              <div style={{ fontFamily: 'var(--font-ui)', fontSize: '1.7rem', fontWeight: 900 }}>{haftalikSaatMetni}</div>
              <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginTop: 4 }}>Slot suresi {dersSuresiDk} dk</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1.6fr) minmax(240px, 0.9fr)', gap: 14, alignItems: 'start' }}>
            <div className="card" style={{ padding: 18 }}>
              <div className="section-header" style={{ marginBottom: 12 }}>
                <div>
                  <div className="section-title">Bugunku Akis</div>
                  <div className="section-subtitle">{GUN_ETIKETLERI[bugun] || 'Bugun'} gunundeki dersleriniz</div>
                </div>
              </div>
              {bugunDersleri.length === 0 ? (
                <div
                  style={{
                    borderRadius: 14,
                    padding: '18px 16px',
                    background: 'linear-gradient(135deg, #F8FAFF 0%, #EEF4FF 100%)',
                    border: '1px solid #D9E6FF',
                    color: 'var(--text-muted)',
                  }}
                >
                  Bugun icin atanmis ders gorunmuyor.
                </div>
              ) : (
                <div style={{ display: 'grid', gap: 10 }}>
                  {bugunDersleri.map((ders, index) => (
                    <div
                      key={ders.id}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '70px minmax(0, 1fr)' : '78px minmax(0, 1fr) 96px',
                        gap: 10,
                        alignItems: 'center',
                        padding: '12px 14px',
                        borderRadius: 14,
                        border: `1px solid ${(ders.renk || '#3B82F6')}35`,
                        background: `linear-gradient(135deg, ${(ders.renk || '#3B82F6')}16 0%, #FFFFFF 92%)`,
                      }}
                    >
                      <div
                        style={{
                          borderRadius: 12,
                          background: '#fff',
                          border: `1px solid ${(ders.renk || '#3B82F6')}30`,
                          padding: '8px 8px',
                        }}
                      >
                        <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Ders {index + 1}</div>
                        <div style={{ fontWeight: 800, fontSize: '0.86rem', marginTop: 3 }}>{ders.baslangic_saati}</div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{ders.bitis_saati}</div>
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 800, fontSize: '0.9rem', color: ders.renk || 'var(--text-primary)' }}>{ders.ders_adi}</div>
                        <div style={{ marginTop: 3, fontSize: '0.8rem', fontWeight: 600 }}>{ders.sinif_adi}</div>
                        <div style={{ marginTop: 6, fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                          {ders.salonno ? `Salon ${ders.salonno}` : 'Salon atamasi yapilmamis'}
                        </div>
                      </div>
                      {!isMobile && (
                      <div style={{ textAlign: 'right' }}>
                        <div
                          style={{
                            display: 'inline-flex',
                            padding: '6px 10px',
                            borderRadius: 999,
                            background: '#fff',
                            border: `1px solid ${(ders.renk || '#3B82F6')}35`,
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            color: ders.renk || 'var(--text-primary)',
                          }}
                        >
                          {GUN_ETIKETLERI[bugun]}
                        </div>
                      </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="card" style={{ padding: 18 }}>
              <div className="section-title" style={{ marginBottom: 12 }}>Haftalik Dagilim</div>
              <div style={{ display: 'grid', gap: 8 }}>
                {gunlukOzet.map(({ gun, adet }) => (
                  <div key={gun}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                      <span style={{ fontSize: '0.79rem', fontWeight: 700 }}>{GUN_ETIKETLERI[gun]}</span>
                      <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{adet} ders</span>
                    </div>
                    <div style={{ height: 8, background: '#EEF2FF', borderRadius: 999, overflow: 'hidden' }}>
                      <div
                        style={{
                          height: '100%',
                          width: `${program.length > 0 ? (adet / program.length) * 100 : 0}%`,
                          background: 'linear-gradient(90deg, #244A8F 0%, #0EA5A4 100%)',
                          borderRadius: 999,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {atananDersler.length > 0 && (
            <div className="card" style={{ padding: 18 }}>
              <div className="section-title" style={{ marginBottom: 12 }}>Ustlendiginiz Dersler</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {atananDersler.map((item, index) => (
                  <div
                    key={`${item.sinif_id || index}_${item.ders_id || index}`}
                    style={{
                      padding: '10px 13px',
                      borderRadius: 12,
                      background: 'linear-gradient(135deg, #F8FAFF 0%, #FFFFFF 100%)',
                      border: '1px solid #DCE7F8',
                      fontSize: '0.78rem',
                      fontWeight: 700,
                      color: '#17315C',
                    }}
                  >
                    {item.sinif_adi} · {item.ders_adi}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div
            className="card"
            style={{
              padding: 20,
              overflowX: 'hidden',
              background: 'linear-gradient(180deg, #FFFFFF 0%, #FBFCFF 100%)',
            }}
          >
            <div className="section-header" style={{ marginBottom: 14 }}>
              <div>
                <div className="section-title">Haftalik Cizelge</div>
                <div className="section-subtitle">Temiz, okunakli ve yalnizca size ozel gorunum.</div>
              </div>
            </div>
            {isMobile && aktifGunler.length > 0 && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 10, overflowX: 'auto', paddingBottom: 2 }}>
                {aktifGunler.map((g) => (
                  <button
                    key={g}
                    className="btn btn-sm"
                    onClick={() => setMobilGun(g)}
                    style={{
                      flex: '0 0 auto',
                      padding: '6px 10px',
                      borderRadius: 999,
                      border: '1px solid var(--border)',
                      background: aktifMobilGun === g ? 'var(--accent)' : 'var(--bg-elevated)',
                      color: aktifMobilGun === g ? '#fff' : 'var(--text-secondary)',
                      fontWeight: 700,
                    }}
                  >
                    {GUN_KISA[g]}
                  </button>
                ))}
              </div>
            )}

            {aktifGunler.length === 0 || tumSlotlar.length === 0 ? (
              <div className="empty-state">
                <p>Program saatleri tanimlanmamis.</p>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed' }}>
                <thead>
                  <tr>
                    <th
                      style={{
                        textAlign: 'left',
                        padding: '12px 10px',
                        background: '#F3F7FF',
                        borderBottom: '1px solid #DCE7F8',
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                        width: 78,
                      }}
                    >
                      Saat
                    </th>
                    {gorunenGunler.map((gun) => (
                      <th
                        key={gun}
                        style={{
                          padding: '12px 10px',
                          background: '#F3F7FF',
                          borderBottom: '1px solid #DCE7F8',
                          textAlign: 'left',
                        }}
                      >
                        <div style={{ fontFamily: 'var(--font-ui)', fontWeight: 800, fontSize: '0.82rem' }}>{GUN_ETIKETLERI[gun]}</div>
                        <div style={{ fontSize: '0.64rem', color: 'var(--text-muted)', marginTop: 2 }}>
                          {gunSaatleri?.[gun]?.bas} - {gunSaatleri?.[gun]?.bit}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tumSlotlar.map((slot) => (
                    <tr key={`${slot.bas}_${slot.bit}`}>
                      <td
                        style={{
                          padding: '12px 8px',
                          borderBottom: '1px solid #EEF2F7',
                          background: '#FCFDFF',
                          verticalAlign: 'top',
                        }}
                      >
                        <div style={{ fontWeight: 800, color: '#17315C', fontSize: '0.82rem' }}>{slot.bas}</div>
                        <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>{slot.bit}</div>
                      </td>
                      {gorunenGunler.map((gun) => {
                        const ders = programMap[`${gun}_${slot.bas}`];
                        return (
                          <td
                            key={`${gun}_${slot.bas}`}
                            style={{
                              padding: 5,
                              borderBottom: '1px solid #EEF2F7',
                              borderLeft: '1px solid #F2F4F8',
                              verticalAlign: 'top',
                              background: ders ? '#fff' : 'rgba(245, 248, 255, 0.45)',
                            }}
                          >
                            {ders ? (
                              <div
                                style={{
                                  borderRadius: 12,
                                  padding: '9px 10px',
                                  minHeight: 84,
                                  border: `1px solid ${(ders.renk || '#3B82F6')}35`,
                                  background: `linear-gradient(135deg, ${(ders.renk || '#3B82F6')}16 0%, #FFFFFF 100%)`,
                                  boxShadow: '0 6px 14px rgba(15,30,61,0.05)',
                                }}
                              >
                                <div style={{ fontWeight: 800, color: ders.renk || 'var(--text-main)', marginBottom: 4, fontSize: '0.8rem', lineHeight: 1.2 }}>
                                  {ders.ders_adi}
                                </div>
                                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#17315C', lineHeight: 1.25 }}>{ders.sinif_adi}</div>
                                <div style={{ fontSize: '0.64rem', color: 'var(--text-muted)', marginTop: 6 }}>
                                  {ders.baslangic_saati} - {ders.bitis_saati}
                                </div>
                                <div style={{ fontSize: '0.64rem', color: 'var(--text-muted)', marginTop: 2 }}>
                                  {ders.salonno ? `Salon ${ders.salonno}` : 'Salon bilgisi yok'}
                                </div>
                              </div>
                            ) : (
                              <div
                                style={{
                                  minHeight: 84,
                                  borderRadius: 12,
                                  background: 'repeating-linear-gradient(135deg, rgba(220,231,248,0.34) 0, rgba(220,231,248,0.34) 8px, rgba(255,255,255,0.75) 8px, rgba(255,255,255,0.75) 16px)',
                                  border: '1px dashed #E2E8F0',
                                }}
                              />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Denemeler (Görüntüleme) ──────────────────────────────────
export function DenemelerPage() {
  const isMobile = useIsMobile();
  const [denemeler, setDenemeler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seciliDeneme, setSeciliDeneme] = useState(null);
  const [sonucLoading, setSonucLoading] = useState(false);
  const [denemeSonuclari, setDenemeSonuclari] = useState([]);

  const getCachedSonuclar = (denemeId) => {
    if (!denemeId) return [];
    try {
      return JSON.parse(localStorage.getItem(`dp_deneme_sonuclari_${denemeId}`) || '[]');
    } catch (error) {
      return [];
    }
  };

  const denemelerWithCounts = denemeler.map((deneme) => {
    const cachedSonuclar = getCachedSonuclar(deneme.id);
    const katilimciSayisi = Math.max(Number(deneme.katilimci_sayisi || 0), cachedSonuclar.length);
    const ortalamaNet = katilimciSayisi > 0
      ? (
        cachedSonuclar.length > 0
          ? (cachedSonuclar.reduce((toplam, item) => toplam + Number(item.toplamNet || 0), 0) / cachedSonuclar.length)
          : Number(deneme.ortalama_net || 0)
      )
      : null;

    return {
      ...deneme,
      katilimci_sayisi: katilimciSayisi,
      ortalama_net: ortalamaNet,
    };
  });

  useEffect(() => {
    axios.get('/denemeler')
      .then(r => { if (r.data.success) setDenemeler(r.data.data || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const openDenemeDetay = async (deneme) => {
    setSeciliDeneme(deneme);
    setSonucLoading(true);
    const cachedSonuclar = getCachedSonuclar(deneme.id);
    try {
      const { data } = await axios.get(`/denemeler/${deneme.id}/sonuclar`);
      const apiSonuclar = data.success ? (data.data || []) : [];
      setDenemeSonuclari(apiSonuclar.length > 0 ? apiSonuclar : cachedSonuclar);
    } catch (err) {
      setDenemeSonuclari(cachedSonuclar);
      toast.error(err.response?.data?.message || 'Deneme sonuçları yüklenemedi');
    } finally {
      setSonucLoading(false);
    }
  };

  const ortalamaNet = denemeSonuclari.length > 0
    ? (denemeSonuclari.reduce((toplam, item) => toplam + Number(item.toplamNet || 0), 0) / denemeSonuclari.length).toFixed(1)
    : null;
  const sonucliDenemeler = denemelerWithCounts.filter((d) => Number(d.katilimci_sayisi || 0) > 0);
  const sonucsuzDenemeler = denemelerWithCounts.filter((d) => Number(d.katilimci_sayisi || 0) <= 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div className="section-header animate-fadeInUp">
        <div>
          <h2 style={{ fontFamily: 'var(--font-ui)', fontSize: '1.3rem' }}>📊 Deneme Sonuçları</h2>
          <div className="section-subtitle">
            {sonucliDenemeler.length} denemede sonuç var, {sonucsuzDenemeler.length} denemede henüz sonuç yok
          </div>
        </div>
      </div>

      {loading ? <div className="page-loader"><div className="spinner" /></div> :
        denemeler.length === 0 ? (
          <div className="card">
            <div className="empty-state">
              <div style={{ fontSize: '2.5rem' }}>📊</div>
              <h3>Deneme yok</h3>
              <p>Henüz deneme tanımlanmamış.</p>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} className="animate-fadeInUp stagger-1">
            {sonucliDenemeler.map((d, i) => (
              <button
                key={i}
                type="button"
                className="card"
                onClick={() => openDenemeDetay(d)}
                style={{
                  display: 'flex',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  flexDirection: isMobile ? 'column' : 'row',
                  width: '100%',
                  textAlign: 'left',
                  cursor: 'pointer',
                  border: '1px solid var(--border)',
                }}
              >
                <div>
                  <h3 style={{ fontFamily: 'var(--font-ui)', fontSize: '1rem', marginBottom: 4 }}>{d.ad}</h3>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <span className="badge badge-info">{d.tur}</span>
                    <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                      {d.tarih ? new Date(d.tarih).toLocaleDateString('tr-TR') : '—'}
                    </span>
                    {d.katilimci_sayisi > 0 && (
                      <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                        👥 {d.katilimci_sayisi} öğrenci
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ textAlign: isMobile ? 'left' : 'right', minWidth: isMobile ? 0 : 140, width: isMobile ? '100%' : 'auto' }}>
                  {d.ortalama_net != null && (
                    <div>
                      <div style={{ fontFamily: 'var(--font-ui)', fontWeight: 800, fontSize: '1.1rem', color: 'var(--accent)' }}>
                        {parseFloat(d.ortalama_net || 0).toFixed(1)}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Ort. Net</div>
                    </div>
                  )}
                  <div style={{ fontSize: '0.78rem', color: 'var(--accent)', fontWeight: 700, marginTop: 6 }}>
                    Sonuçları Gör →
                  </div>
                </div>
              </button>
            ))}

            {sonucsuzDenemeler.length > 0 && (
              <div className="card" style={{ padding: 18, border: '1px dashed var(--border)' }}>
                <div style={{ fontWeight: 800, marginBottom: 10, color: 'var(--text-secondary)' }}>
                  Henüz Sonuç Girilmeyen Denemeler
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {sonucsuzDenemeler.map((d, i) => (
                    <div
                      key={`bos-${i}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                        padding: '14px 16px',
                        borderRadius: 12,
                        background: 'var(--bg-elevated)',
                        border: '1px solid var(--border)',
                        opacity: 0.72,
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 700 }}>{d.ad}</div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                          <span className="badge badge-info">{d.tur}</span>
                          <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                            {d.tarih ? new Date(d.tarih).toLocaleDateString('tr-TR') : '—'}
                          </span>
                        </div>
                      </div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 700 }}>
                        Sonuç yok
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )
      }

      {seciliDeneme && (
        <div className="modal-backdrop" onClick={() => setSeciliDeneme(null)}>
          <div className="modal" style={{ maxWidth: 860, width: '100%', padding: isMobile ? 16 : 24 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">📊 {seciliDeneme.ad}</div>
              <button className="modal-close" onClick={() => setSeciliDeneme(null)}>✕</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'grid', gap: 10, gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, minmax(0, 1fr))' }}>
                <div className="card" style={{ flex: 1, minWidth: 160, padding: 16 }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Deneme Tarihi</div>
                  <div style={{ fontFamily: 'var(--font-ui)', fontSize: '1.1rem', fontWeight: 800 }}>
                    {seciliDeneme.tarih ? new Date(seciliDeneme.tarih).toLocaleDateString('tr-TR') : '—'}
                  </div>
                </div>
                <div className="card" style={{ flex: 1, minWidth: 160, padding: 16 }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Deneme Türü</div>
                  <div style={{ fontFamily: 'var(--font-ui)', fontSize: '1.1rem', fontWeight: 800 }}>
                    {seciliDeneme.tur || '—'}
                  </div>
                </div>
                <div className="card" style={{ flex: 1, minWidth: 160, padding: 16 }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Sonuç Sayısı</div>
                  <div style={{ fontFamily: 'var(--font-ui)', fontSize: '1.1rem', fontWeight: 800 }}>
                    {denemeSonuclari.length}
                  </div>
                </div>
                <div className="card" style={{ flex: 1, minWidth: 160, padding: 16 }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Ortalama Net</div>
                  <div style={{ fontFamily: 'var(--font-ui)', fontSize: '1.1rem', fontWeight: 800 }}>
                    {ortalamaNet ?? '—'}
                  </div>
                </div>
              </div>

              {sonucLoading ? (
                <div className="page-loader" style={{ minHeight: 180 }}><div className="spinner" /></div>
              ) : denemeSonuclari.length === 0 ? (
                <div className="empty-state" style={{ padding: 36 }}>
                  <div style={{ fontSize: '2rem' }}>📝</div>
                  <h3>Henüz sonuç girilmemiş</h3>
                  <p>Bu deneme için sisteme işlenmiş sonuç bulunmuyor.</p>
                </div>
              ) : (
                <div className="table-wrapper" style={{ overflowX: 'auto' }}>
                  <table className="table" style={{ fontSize: '0.88rem' }}>
                    <thead>
                      <tr>
                        <th style={{ width: 70 }}>Sıra</th>
                        <th>Öğrenci</th>
                        <th>Sınıf</th>
                        <th>Net</th>
                        <th>Puan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {denemeSonuclari.map((sonuc, index) => (
                        <tr key={sonuc.id || index}>
                          <td>#{sonuc.siralama || index + 1}</td>
                          <td>
                            <div style={{ fontWeight: 700 }}>{sonuc.ad} {sonuc.soyad}</div>
                            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                              No: {sonuc.numara || '—'}
                            </div>
                          </td>
                          <td>{sonuc.sinif || '—'}</td>
                          <td>{Number(sonuc.toplamNet || 0).toFixed(2)}</td>
                          <td>{Number(sonuc.puan || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
